import { left, type TaskEither } from 'fp-ts/lib/TaskEither';

import { type TypedError, typedError } from '../../lib/typedError';
import * as Form from '../Form';
import { DappPreviewPanel } from './DappPreviewPanel';
import { FormValidityObserver } from './components/FormValidityObserver';
import { FORM_ID, NewDappFormData } from './constants';
import { useNewDappPreviewData } from './hooks/useNewDappPreviewData';
import { usePackageNameAvailability } from './hooks/usePackageNameAvailability';
import { AdditionalInfoSection } from './sections/AdditionalInfoSection';
import { BasicDetailsSection } from './sections/BasicDetailsSection';
import { EditorsChoiceSection } from './sections/EditorsChoiceSection';
import { LegalComplianceSection } from './sections/LegalComplianceSection';
import type {
  FormState,
  NewDappDataType,
  NewDappInputChangeEvent,
  SingleFileFieldName,
} from './types';

export {
  MAX_DAPP_NAME_LENGTH,
  MAX_EDITORS_CHOICE_HEADLINE_LENGTH,
  MAX_EDITORS_CHOICE_TAG_LENGTH,
  MAX_SUBTITLE_LENGTH,
} from '../../constants/form';

interface NewDappFormProps {
  formData: FormState;
  handleInputChange: (event: NewDappInputChangeEvent) => void;
  handlePreviewFileChange: (index: number, file: File | null) => void;
  handleAddPreviewSlot: () => void;
  handleRemovePreviewSlot: (index: number) => void;
  handleSingleFileChange: (
    fieldName: SingleFileFieldName,
    file: File | null,
  ) => void;
  handleSubmit: (
    input: TaskEither<TypedError<'InvalidFormData'>, NewDappDataType>,
  ) => TaskEither<unknown, unknown>;
  onValidityChange?: (isValid: boolean) => void;
  previewDimensions?: { width: number; height: number } | null;
}

export function NewDappForm({
  formData,
  handleInputChange,
  handlePreviewFileChange,
  handleAddPreviewSlot,
  handleRemovePreviewSlot,
  handleSingleFileChange,
  handleSubmit,
  onValidityChange,
  previewDimensions,
}: NewDappFormProps) {
  const previewImageCount = formData.dappPreviews.filter(
    (preview) => preview !== null,
  ).length;
  const previewData = useNewDappPreviewData(formData);
  const {
    packageNameError,
    isCheckingPackageName,
    showSpinner,
    packageNameValid,
  } = usePackageNameAvailability(formData.packageName);

  const wrappedHandleSubmit = (
    input: TaskEither<TypedError<'InvalidFormData'>, NewDappDataType>,
  ) => {
    if (packageNameError) {
      return left(typedError('InvalidFormData', packageNameError));
    }

    return handleSubmit(input);
  };

  return (
    <div className="flex flex-col md:flex-row gap-8">
      <Form.Root
        id={FORM_ID}
        formDataShape={NewDappFormData}
        onSubmitData={wrappedHandleSubmit}
        className="flex-1 mb-8"
        validityKey={JSON.stringify(formData)}
      >
        <FormValidityObserver
          onValidityChange={onValidityChange}
          packageNameValid={packageNameValid}
          packageNameError={packageNameError}
          isCheckingPackageName={isCheckingPackageName}
          packageName={formData.packageName}
          hasDappIcon={!!formData.dappIcon}
          hasBanner={!!formData.banner}
          previewImageCount={previewImageCount}
        />

        <div className="w-full max-w-[640px] mx-auto flex flex-col gap-16">
          <h1 className="justify-start text-white text-4xl font-medium leading-10">
            Let&apos;s add your dApp details!
          </h1>

          <BasicDetailsSection
            formData={formData}
            previewDimensions={previewDimensions}
            packageNameError={packageNameError}
            isCheckingPackageName={isCheckingPackageName}
            showPackageNameSpinner={showSpinner}
            packageNameValid={packageNameValid}
            onInputChange={handleInputChange}
            onPreviewFileChange={handlePreviewFileChange}
            onAddPreviewSlot={handleAddPreviewSlot}
            onRemovePreviewSlot={handleRemovePreviewSlot}
            onSingleFileChange={handleSingleFileChange}
          />

          <EditorsChoiceSection
            formData={formData}
            onInputChange={handleInputChange}
            onSingleFileChange={handleSingleFileChange}
          />

          <AdditionalInfoSection
            formData={formData}
            onInputChange={handleInputChange}
          />

          <LegalComplianceSection
            formData={formData}
            onInputChange={handleInputChange}
          />

          <Form.Error className="mt-1 text-right" />
        </div>
      </Form.Root>

      <div className="md:w-[300px] flex-shrink-0 xl:mr-48 lg:mr-32">
        <DappPreviewPanel
          formData={previewData}
          previewDimensions={previewDimensions}
        />
      </div>
    </div>
  );
}

export type {
  FormState,
  NewDappDataType,
  NewDappInputChangeEvent,
  SingleFileFieldName,
};
